import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 排序算法可视化动画 - 冒泡排序
 * 
 * 核心效果：
 * 1. 数组元素可视化
 * 2. 比较过程高亮
 * 3. 交换动画
 * 4. 已排序部分标记
 */

export interface SortingStep {
  array: number[];
  comparing?: [number, number]; // 正在比较的索引
  swapping?: [number, number]; // 正在交换的索引
  sorted?: number[]; // 已排序的索引
}

export interface SortingAlgorithmAnimationProps {
  title?: string;
  subtitle?: string;
  algorithm?: 'bubble' | 'selection' | 'insertion';
  initialArray?: number[];
  primaryColor?: string;
  secondaryColor?: string;
  sortedColor?: string;
  backgroundColor?: string;
  showCode?: boolean;
}

// 生成冒泡排序步骤
const generateBubbleSortSteps = (arr: number[]): SortingStep[] => {
  const steps: SortingStep[] = [];
  const array = [...arr];
  const n = array.length;

  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      // 比较步骤
      steps.push({
        array: [...array],
        comparing: [j, j + 1],
        sorted: Array.from({ length: i }, (_, idx) => n - 1 - idx),
      });

      if (array[j] > array[j + 1]) {
        // 交换步骤
        steps.push({
          array: [...array],
          swapping: [j, j + 1],
          sorted: Array.from({ length: i }, (_, idx) => n - 1 - idx),
        });

        // 执行交换
        [array[j], array[j + 1]] = [array[j + 1], array[j]];

        // 交换后
        steps.push({
          array: [...array],
          sorted: Array.from({ length: i }, (_, idx) => n - 1 - idx),
        });
      }
    }
  }

  // 最终已排序
  steps.push({
    array: [...array],
    sorted: Array.from({ length: n }, (_, idx) => idx),
  });

  return steps;
};

export const SortingAlgorithmAnimation: React.FC<SortingAlgorithmAnimationProps> = ({
  title = '冒泡排序算法',
  subtitle = 'Bubble Sort Visualization',
  algorithm = 'bubble',
  initialArray = [64, 34, 25, 12, 22, 11, 90],
  primaryColor = '#4A90E2',
  secondaryColor = '#F59E0B',
  sortedColor = '#10B981',
  backgroundColor = '#0F1419',
  showCode = true,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 生成排序步骤
  const steps = generateBubbleSortSteps(initialArray);
  const framesPerStep = 12;
  const currentStepIndex = Math.floor(frame / framesPerStep);
  const stepProgress = (frame % framesPerStep) / framesPerStep;

  const currentStep = steps[Math.min(currentStepIndex, steps.length - 1)];
  const array = currentStep.array;

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' });
  const titleY = spring({ frame, from: -50, to: 0, fps, config: { damping: 15 } });

  // 计算柱状图尺寸
  const barWidth = Math.min(80, (width - 200) / array.length - 10);
  const maxBarHeight = height * 0.5;
  const maxValue = Math.max(...initialArray);

  // 步骤说明
  const getStepDescription = (step: SortingStep): string => {
    if (step.swapping) {
      return `交换 ${step.array[step.swapping[0]]} 和 ${step.array[step.swapping[1]]}`;
    }
    if (step.comparing) {
      return `比较 ${step.array[step.comparing[0]]} 和 ${step.array[step.comparing[1]]}`;
    }
    if (step.sorted && step.sorted.length === array.length) {
      return '排序完成！';
    }
    return '继续比较...';
  };

  // 进度条
  const progress = currentStepIndex / (steps.length - 1);

  return (
    <AbsoluteFill style={{
      background: backgroundColor,
      fontFamily: 'system-ui, sans-serif',
      color: 'white',
      padding: 60,
    }}>
      {/* 标题 */}
      <div style={{
        position: 'absolute',
        top: 60,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `translateY(${titleY}px)`,
      }}>
        <h1 style={{
          fontSize: 72,
          fontWeight: 700,
          margin: 0,
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          {title}
        </h1>
        <p style={{
          fontSize: 32,
          marginTop: 16,
          opacity: 0.7,
        }}>
          {subtitle}
        </p>
      </div>

      {/* 数组可视化 */}
      <div style={{
        position: 'absolute',
        top: '22%',
        left: '55%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-end',
        height: maxBarHeight + 60,
      }}>
        {array.map((value, index) => {
          const barHeight = (value / maxValue) * maxBarHeight;
          const isComparing = currentStep.comparing?.includes(index);
          const isSwapping = currentStep.swapping?.includes(index);
          const isSorted = currentStep.sorted?.includes(index);

          // 交换动画
          const swapOffsetX = isSwapping ? interpolate(
            stepProgress,
            [0, 0.5, 1],
            [0, 50 * (index === currentStep.swapping![0] ? -1 : 1), 0]
          ) : 0;

          // 弹跳效果
          const bounceScale = isComparing ? spring({
            frame: frame % framesPerStep,
            from: 1,
            to: 1.1,
            fps,
            config: { damping: 10, stiffness: 200 },
          }) : 1;

          let barColor = primaryColor;
          if (isSorted) barColor = sortedColor;
          if (isComparing) barColor = secondaryColor;
          if (isSwapping) barColor = '#EF4444';

          return (
            <div key={index} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transform: `translateX(${swapOffsetX}px)`,
            }}>
              {/* 数值 */}
              <div style={{
                fontSize: 28,
                fontWeight: 600,
                marginBottom: 10,
                color: isComparing || isSwapping ? '#FFD700' : 'white',
              }}>
                {value}
              </div>
              
              {/* 柱状图 */}
              <div style={{
                width: barWidth,
                height: barHeight * bounceScale,
                background: `linear-gradient(180deg, ${barColor} 0%, ${barColor}CC 100%)`,
                borderRadius: 8,
                boxShadow: isComparing || isSwapping 
                  ? `0 0 30px ${barColor}80` 
                  : '0 4px 12px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.15s ease',
              }} />
              
              {/* 索引 */}
              <div style={{
                marginTop: 10,
                fontSize: 20,
                opacity: 0.6,
              }}>
                [{index}]
              </div>
            </div>
          );
        })}
      </div>

      {/* 步骤说明 */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '12%',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-block',
          padding: '20px 40px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 16,
          border: '2px solid rgba(255, 255, 255, 0.2)',
        }}>
          <div style={{
            fontSize: 32,
            fontWeight: 600,
          }}>
            {getStepDescription(currentStep)}
          </div>
          <div style={{
            fontSize: 24,
            marginTop: 10,
            opacity: 0.7,
          }}>
            步骤 {currentStepIndex + 1} / {steps.length}
          </div>
        </div>
      </div>

      {/* 代码展示 */}
      {showCode && (
        <div style={{
          position: 'absolute',
          bottom: 80,
          left: 60,
          right: 60,
          padding: 30,
          background: 'rgba(0, 0, 0, 0.4)',
          borderRadius: 16,
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <div style={{
            fontSize: 20,
            fontFamily: 'Monaco, monospace',
            opacity: 0.9,
          }}>
            <code style={{ color: '#10B981' }}>for</code>
            <code style={{ color: 'white' }}> (i = 0; i &lt; n-1; i++)</code>
            <code style={{ color: '#10B981' }}> {`{`}</code>
            <br />
            <code style={{ color: 'white' }}>  </code>
            <code style={{ color: '#10B981' }}>for</code>
            <code style={{ color: 'white' }}> (j = 0; j &lt; n-i-1; j++)</code>
            <code style={{ color: '#10B981' }}> {`{`}</code>
            <br />
            <code style={{ color: 'white' }}>    </code>
            <code style={{ color: '#10B981' }}>if</code>
            <code style={{ color: 'white' }}> (arr[j] &gt; arr[j+1])</code>
            <code style={{ color: '#10B981' }}> swap</code>
            <code style={{ color: 'white' }}>(arr[j], arr[j+1])</code>
          </div>
        </div>
      )}

      {/* 进度条 */}
      <div style={{
        position: 'absolute',
        bottom: 20,
        left: 60,
        right: 60,
        height: 8,
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${progress * 100}%`,
          height: '100%',
          background: `linear-gradient(90deg, ${primaryColor} 0%, ${sortedColor} 100%)`,
          borderRadius: 4,
        }} />
      </div>
    </AbsoluteFill>
  );
};
